import "reflect-metadata";
import "@/styles/fonts.css";
import "@/styles/tokens.css";
import "@/styles/squircle.css";
import "@/styles/inspectorPage.css";
import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import { registerCoreServices } from "@core/ioc/registrations";
import { Ioc } from "@core/ioc/ioc";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import { setSquircleEnabled } from "@/styles/squircleStyle";
import { router } from "@router/index";
import { startCampfireMcpPageBridge } from "@ai/mcp/CampfireMcpPageBridge";

registerCoreServices();
startCampfireMcpPageBridge();

setSquircleEnabled(true);

// iOS Home-Screen launch: publish the height, in CSS px, that covers the whole
// physical screen.
//
// `apple-mobile-web-app-status-bar-style: black-translucent` (index.html) moves
// the web view's ORIGIN to the physical top of the screen — content is drawn
// from y=0 and the clock composites over our own pixels — but it does NOT grow
// the view, which iOS still sizes `screen - statusBar`. The page is anchored at
// the top, so the whole shortfall lands at the BOTTOM. Measured in the iOS 26.5
// simulator (2026-08-13, iPhone 17 Pro Max): a 956pt screen rendering 894pt of
// page. A `position: fixed` layer is clipped at that layout viewport and can
// never reach the strip — which is exactly why the projects list already fills
// the screen (its roots are unclamped to ordinary document content) while the
// editor did not. `tokens.css` spends this value on making the editor's shell
// document content too.
//
// ⚠ It is published as an ABSOLUTE height, deliberately, and must stay that
// way. The first cut computed the SHORTFALL (`screen − viewport`) and CSS added
// it to `100svh` — which oscillated the editor between correct and clipped on
// every frame, because the viewport height is an input that our own height
// change moves: grow the page, the viewport re-measures, the shortfall shrinks,
// the page shrinks, repeat. Every input below is independent of anything this
// writes (`screen.*` is the device; `scale` is the user's Page Zoom), so the
// value settles on the first pass and the write is skipped once it is stable.
//
// The height is MEASURED, not assumed, because the obvious constant is wrong.
// `env(safe-area-inset-top)` matches the missing strip on this device (62) and
// looks like the answer, but it is the status bar rather than the missing
// viewport, and the two part company the moment the page is not at 100% — and
// this page was not: the same run reported `visualViewport.scale = 0.85`, an
// iOS per-site Page Zoom, so 1052 CSS px of viewport rendered as 894pt. Hence
// the arithmetic is done in POINTS, where `screen` and the rendered viewport
// are directly comparable, then converted back through `scale` for CSS.
//
// `navigator.standalone` is the gate, and it has to be: only a Home-Screen
// launch owns the entire screen, so only there does `screen.height` describe
// the space the page should fill. It measured `true` while
// `(display-mode: standalone)` measured FALSE, so the standard media query is
// not a substitute. A Safari TAB, Chromium and desktop never take this path.
if ((navigator as { standalone?: boolean }).standalone === true) {
  let m_publishedHeight = "";
  const applyStandaloneScreenHeight = (): void => {
    const vv = window.visualViewport;
    if (!vv) return;
    // iOS reports `screen.*` in its own orientation-independent way, so pick
    // the dimension by the viewport's SHAPE rather than trusting either field.
    // This is the one place the viewport is read, and only as a portrait /
    // landscape test — never as a term in the arithmetic.
    const screenHeightPt =
      vv.width <= vv.height
        ? Math.max(screen.width, screen.height)
        : Math.min(screen.width, screen.height);
    const cssHeight = `${Math.round(screenHeightPt / (vv.scale || 1))}px`;
    if (cssHeight === m_publishedHeight) return;
    m_publishedHeight = cssHeight;
    document.documentElement.style.setProperty(
      "--cf-ios-standalone-h",
      cssHeight,
    );
  };
  document.documentElement.classList.add("cf-ios-standalone");
  applyStandaloneScreenHeight();
  addEventListener("resize", applyStandaloneScreenHeight);
  addEventListener("orientationchange", applyStandaloneScreenHeight);
  window.visualViewport?.addEventListener("resize", applyStandaloneScreenHeight);
}

// Disable page pinch-zoom. The app is a full-viewport editor — a pinch is a
// scene/UI gesture, never a request to zoom the page (on Vision Pro Safari a
// stray two-finger pinch zoomed the whole shell). WebKit ignores the
// viewport meta's `user-scalable=no`, but its proprietary GestureEvents are
// cancellable — preventing them suppresses the page zoom while leaving
// app-level pointer/touch handling untouched. `touch-action` in tokens.css
// covers Chromium/Quest; double-tap zoom is covered by both.
for (const type of ["gesturestart", "gesturechange", "gestureend"]) {
  document.addEventListener(type, (e) => e.preventDefault(), { passive: false });
}

const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
auth.onSessionExpired.on(() => {
  const currentRoute = router.currentRoute.value;
  if (currentRoute.meta.requiresAuth === false) return;

  void router.replace({
    name: "Login",
    query:
      currentRoute.fullPath !== "/"
        ? { from: currentRoute.fullPath }
        : undefined,
  });
});

createApp(App).use(createPinia()).use(router).mount("#app");
